import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/features/auth/AuthContext";
import { getSessionGeneration } from "@/lib/http";
import { loadNavigation, navigationError, saveNavigation } from "./api";
import { EMPTY_NAVIGATION, type NavigationConfig } from "./types";

export type NavigationStatus = "loading" | "ready" | "saving" | "error";

export function useNavigation() {
  const { estado, aberto, usuario, permissoes } = useAuth();
  const generation = getSessionGeneration();
  const identity = `${usuario?.id ?? ""}/${usuario?.organizacao?.organizationId ?? ""}/${aberto}/${generation}`;
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const [snapshot, setSnapshot] = useState<{ identity: string; config: NavigationConfig | null; status: NavigationStatus; error: string | null }>({
    identity, config: null, status: "loading", error: null,
  });
  const [version, setVersion] = useState(0);
  const requestId = useRef(0);
  const submitting = useRef<string | null>(null);
  const reload = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    const id = ++requestId.current;
    if (!estado) return;
    if (aberto || estado.modo !== "autenticado") {
      setSnapshot({ identity, config: aberto ? EMPTY_NAVIGATION : null, status: "ready", error: null });
      return;
    }
    // Foco/reload durante PUT nao pode liberar o formulario nem repor revisao antiga.
    if (submitting.current === identity) return;
    const controller = new AbortController();
    setSnapshot(previous => ({ identity, config: previous.identity === identity ? previous.config : null, status: "loading", error: null }));
    void loadNavigation(controller.signal).then(config => {
      if (!controller.signal.aborted && currentIdentity.current === identity && requestId.current === id) {
        setSnapshot({ identity, config, status: "ready", error: null });
      }
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && currentIdentity.current === identity && requestId.current === id) {
        setSnapshot(previous => ({ ...previous, status: "error", error: navigationError(error) }));
      }
    });
    return () => controller.abort();
  }, [estado, aberto, identity, version]);

  useEffect(() => {
    window.addEventListener("ghm:navigation-updated", reload);
    window.addEventListener("ghm:workflows-updated", reload);
    window.addEventListener("focus", reload);
    return () => {
      window.removeEventListener("ghm:navigation-updated", reload);
      window.removeEventListener("ghm:workflows-updated", reload);
      window.removeEventListener("focus", reload);
    };
  }, [reload]);

  const save = useCallback(async (config: NavigationConfig): Promise<NavigationConfig> => {
    if (currentIdentity.current !== identity || getSessionGeneration() !== generation) {
      throw new DOMException("Sessão alterada", "AbortError");
    }
    if (aberto || estado?.modo !== "autenticado" || !permissoes.gerenciarWorkspaces) {
      throw new Error("A organização do menu exige um administrador autenticado.");
    }
    if (submitting.current === identity) throw new Error("Já existe um salvamento em andamento.");
    submitting.current = identity;
    ++requestId.current;
    setSnapshot(previous => ({ ...previous, status: "saving", error: null }));
    try {
      const saved = await saveNavigation(config);
      if (currentIdentity.current !== identity || getSessionGeneration() !== generation) throw new DOMException("Sessão alterada", "AbortError");
      ++requestId.current;
      setSnapshot({ identity, config: saved, status: "ready", error: null });
      window.dispatchEvent(new Event("ghm:navigation-updated"));
      return saved;
    } catch (error) {
      if (currentIdentity.current === identity) setSnapshot(previous => ({ ...previous, status: "error", error: navigationError(error) }));
      throw error;
    } finally {
      if (submitting.current === identity) submitting.current = null;
    }
  }, [aberto, estado, permissoes.gerenciarWorkspaces, identity, generation]);

  return snapshot.identity === identity ? { config: snapshot.config, status: snapshot.status, error: snapshot.error, reload, save }
    : { config: null, status: "loading" as const, error: null, reload, save };
}
